import React from "react";
import "./Steps.css";

function Step3Packaging({ formData, packagingMaterials, updateArrayData, addPackagingMaterial, removePackagingMaterial, validationErrors = [], supplierPackaging = null }) {
  // Helper to get packaging material-specific errors
  const getPackagingError = (index, field) => {
    return validationErrors.find(err => 
      err.toLowerCase().includes(`packaging material ${index + 1}`) && 
      err.toLowerCase().includes(field.toLowerCase())
    );
  };

  // Helper to check if a field is filled
  const isFilled = (value) => {
    return value !== "" && value !== null && value !== undefined;
  };

  // Count how many materials are actually filled out (Loose Packed counts as filled without a weight)
  const filledMaterialsCount = formData.packaging.materials.filter(
    m => m.type && (m.weight || m.type === 'Loose Packed')
  ).length;

  // Not tied to a specific material index, so it isn't caught by getPackagingError
  const missingPackagingError = validationErrors.find(err =>
    err.toLowerCase().includes('at least one packaging material')
  );

  return (
    <div className="step-container">
      {/* Header */}
      <div className="step-header">
        <div className="step-number">03</div>
        <div>
          <h2 className="step-title">Packaging</h2>
          <p className="step-subtitle">Add packaging materials used for your product</p>
        </div>
      </div>

      {/* Supplier packaging hint */}
      {supplierPackaging && (
        <div className="info-box" style={{ marginBottom: '1.5rem' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 14V10M10 6H10.01M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div>
            <div className="info-box-title">Supplier packaging data</div>
            <div className="info-box-text">According to the supplier, this product is packaged as: <strong>{supplierPackaging}</strong>. Use this to guide your selection below.</div>
          </div>
        </div>
      )}

      {/* Form Fields */}
      <div className="form-section">
        <div className="section-header">
          <div>
            <h3 className="section-title">Packaging Materials</h3>
            <p className="section-description">
              {filledMaterialsCount > 0 
                ? `${filledMaterialsCount} packaging ${filledMaterialsCount === 1 ? 'material' : 'materials'} added`
                : 'Add boxes, bags, wrapping, or other packaging materials'}
            </p>
          </div>
          
        </div>

        {formData.packaging.materials.length === 0 && (
          <div className="empty-state">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <rect x="10" y="14" width="28" height="24" rx="2" stroke="#cbd5e1" strokeWidth="2"/>
              <path d="M10 22H38M24 14V22M18 28H30" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p className="empty-state-text">No packaging materials added yet. Click "Add Packaging" — if the product ships without any rigid packaging, add one material and mark it Loose Packed.</p>
          </div>
        )}

        {missingPackagingError && (
          <div className="field-error" style={{ marginBottom: '1rem' }}>{missingPackagingError}</div>
        )}

        {formData.packaging.materials.map((material, index) => (
          <div key={index} className="item-card">
            <div className="item-card-header">
              <span className="item-badge">Material {index + 1}</span>
              <button
                type="button"
                className="btn-remove"
                onClick={() => removePackagingMaterial(index)}
                aria-label="Remove packaging material"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="field-label">
                  Material Type
                  <span className="field-required">*</span>
                </label>
                <select
                  className={`field-input field-select ${getPackagingError(index, "type") ? "field-input--error" : ""} ${isFilled(material.type) ? "field-input--filled" : ""}`}
                  value={material.type}
                  onChange={(e) =>
                    updateArrayData("packaging", "materials", index, "type", e.target.value)
                  }
                >
                  <option value="">Select packaging material...</option>
                  <option value="Loose Packed">Loose Packed</option>
                  {packagingMaterials.map((mat) => (
                    <option key={mat} value={mat}>{mat}</option>
                  ))}
                </select>
                {getPackagingError(index, "type") && (
                  <div className="field-error">Material type is required</div>
                )}
              </div>

              {material.type !== 'Loose Packed' && (
                <div className="form-field">
                  <label className="field-label">
                    Weight (grams)
                    <span className="field-required">*</span>
                  </label>
                  <input
                    type="number"
                    className={`field-input ${getPackagingError(index, "weight") ? "field-input--error" : ""} ${isFilled(material.weight) ? "field-input--filled" : ""}`}
                    value={material.weight}
                    placeholder="e.g., 20"
                    onChange={(e) =>
                      updateArrayData("packaging", "materials", index, "weight", e.target.value)
                    }
                  />
                  {getPackagingError(index, "weight") && (
                    <div className="field-error">Weight is required</div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        <button
            type="button"
            className="btn-add"
            onClick={addPackagingMaterial}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Add Packaging
          </button>

        {/* Info Box */}
        {formData.packaging.materials.length > 0 && (
          <div className="info-box">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 14V10M10 6H10.01M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div>
              <div className="info-box-title">Tip: Include all packaging layers</div>
              <div className="info-box-text">Remember to add inner packaging, boxes, protective materials, and any outer shipping materials.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Step3Packaging;


